import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
  Fade,
  Zoom,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  RocketLaunch,
  TrendingUp,
  Security,
  Speed,
  Email,
  Inbox,
  Search,
  Analytics,
  CheckCircle,
  PlayArrow,
  ArrowForward,
} from '@mui/icons-material';

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.6); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const NewHero = ({ onGetStarted }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const [typedText, setTypedText] = useState('');
  const fullText = 'Email Studio';

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 150);
    return () => clearInterval(interval);
  }, []);

  // Theme colors
  const primaryGradient = isDark
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)';

  const backgroundGradient = isDark
    ? `linear-gradient(135deg, ${alpha('#0f172a', 0.98)} 0%, ${alpha('#1e1b4b', 0.95)} 100%)`
    : 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 50%, #fce7f3 100%)';

  const cardBg = isDark ? alpha('#1e293b', 0.6) : alpha('#ffffff', 0.9);
  const textColor = isDark ? '#f1f5f9' : '#0f172a';
  const accentColor = isDark ? '#818cf8' : '#3b82f6';

  const features = [
    { icon: <Email />, text: 'Smart Email Campaigns', color: '#3b82f6' },
    { icon: <Search />, text: 'FMCSA Verified Carrier Search', color: '#8b5cf6', badge: 'NEW' },
    { icon: <Inbox />, text: 'Unified Inbox For All Accounts', color: '#06b6d4' },
    { icon: <Analytics />, text: 'Real-Time Analytics & Tracking', color: '#ec4899' },
  ];

  const stats = [
    { value: '50K+', label: 'Emails Sent' },
    { value: '99.9%', label: 'Uptime' },
    { value: '24/7', label: 'Support' },
    { value: '10x', label: 'Faster' },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: backgroundGradient,
        backgroundSize: '200% 200%',
        animation: `${gradientShift} 15s ease infinite`,
        pt: { xs: 12, md: 16 },
        pb: { xs: 10, md: 14 },
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.15 : 0.08,
          background: `
            radial-gradient(circle at 20% 30%, ${alpha(accentColor, 0.4)} 0%, transparent 50%),
            radial-gradient(circle at 80% 60%, ${alpha('#8b5cf6', 0.4)} 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, ${alpha('#ec4899', 0.3)} 0%, transparent 50%)
          `,
        }}
      />

      {/* Floating Particles */}
      {[...Array(20)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: Math.random() * 6 + 2,
            height: Math.random() * 6 + 2,
            borderRadius: '50%',
            background: alpha(accentColor, 0.4),
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `${floatAnimation} ${Math.random() * 10 + 5}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Stack spacing={4}>
                {/* Badge */}
                <Zoom in timeout={1200}>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Chip
                      icon={<RocketLaunch sx={{ fontSize: 18 }} />}
                      label="Email Marketing & FMCSA Carrier Search"
                      sx={{
                        background: primaryGradient,
                        color: '#fff',
                        fontWeight: 700,
                        px: 2,
                        py: 2.5,
                        fontSize: '0.85rem',
                        letterSpacing: 0.5,
                        boxShadow: `0 8px 32px ${alpha(accentColor, 0.35)}`,
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      label="FMCSA Verified Data"
                      sx={{
                        background: isDark ? alpha('#10b981', 0.2) : alpha('#10b981', 0.15),
                        color: '#10b981',
                        fontWeight: 700,
                        px: 2,
                        py: 2.5,
                        fontSize: '0.85rem',
                        border: `1px solid ${alpha('#10b981', 0.3)}`,
                        '& .MuiChip-icon': { color: '#10b981' },
                      }}
                    />
                  </Stack>
                </Zoom>

                {/* Main Heading */}
                <Typography
                  variant={isMobile ? 'h3' : isSmallScreen ? 'h2' : 'h1'}
                  sx={{
                    fontFamily: '"Sora", "Inter", sans-serif',
                    fontWeight: 900,
                    lineHeight: 1.15,
                    color: textColor,
                    textShadow: isDark ? `0 2px 20px ${alpha('#000', 0.3)}` : 'none',
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                  }}
                >
                  Powerful Email Outreach for{' '}
                  <Box
                    component="span"
                    sx={{
                      background: primaryGradient,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: `${gradientShift} 8s ease infinite`,
                      backgroundSize: '200% 200%',
                    }}
                  >
                    {typedText}
                    <Box component="span" sx={{ animation: `${pulseGlow} 1s infinite` }}>
                      |
                    </Box>
                  </Box>
                </Typography>

                {/* Subtitle */}
                <Typography
                  variant="h6"
                  sx={{
                    color: isDark ? alpha(textColor, 0.8) : alpha(textColor, 0.7),
                    lineHeight: 1.8,
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    fontWeight: 400,
                  }}
                >
                  Manage multiple accounts, send smart campaigns, and track engagement from one place.
                </Typography>

                {/* Feature Pills */}
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {features.map((feature, idx) => (
                    <Zoom in timeout={1400 + idx * 100} key={idx}>
                      <Box sx={{ position: 'relative' }}>
                        <Chip
                          icon={feature.icon}
                          label={feature.text}
                          sx={{
                            bgcolor: alpha(feature.color, isDark ? 0.2 : 0.1),
                            color: isDark ? alpha(feature.color, 0.9) : feature.color,
                            border: `1px solid ${alpha(feature.color, isDark ? 0.3 : 0.2)}`,
                            fontWeight: 600,
                            py: 2.5,
                            px: 1,
                            fontSize: '0.9rem',
                            transition: 'all 0.3s',
                            '&:hover': {
                              bgcolor: alpha(feature.color, isDark ? 0.3 : 0.15),
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 20px ${alpha(feature.color, 0.3)}`,
                            },
                            '& .MuiChip-icon': {
                              color: isDark ? alpha(feature.color, 0.9) : feature.color,
                            },
                          }}
                        />
                        {feature.badge && (
                          <Chip
                            label={feature.badge}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: '#fff',
                              fontWeight: 800,
                              fontSize: '0.65rem',
                              height: 20,
                              px: 0.5,
                              animation: `${pulseGlow} 2s infinite`,
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                            }}
                          />
                        )}
                      </Box>
                    </Zoom>
                  ))}
                </Stack>

                {/* CTA Buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onGetStarted}
                    endIcon={<ArrowForward />}
                    sx={{
                      background: primaryGradient,
                      backgroundSize: '200% 200%',
                      animation: `${gradientShift} 5s ease infinite`,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      py: 2,
                      px: 4,
                      borderRadius: 3,
                      textTransform: 'none',
                      boxShadow: `0 12px 40px ${alpha(accentColor, 0.4)}`,
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 16px 50px ${alpha(accentColor, 0.5)}`,
                      },
                    }}
                  >
                    Get Started Free
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayArrow />}
                    sx={{
                      borderColor: accentColor,
                      color: accentColor,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      py: 2,
                      px: 4,
                      borderRadius: 3,
                      borderWidth: 2,
                      textTransform: 'none',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: accentColor,
                        bgcolor: alpha(accentColor, 0.1),
                        transform: 'translateY(-3px)',
                      },
                    }}
                  >
                    Watch Demo
                  </Button>
                </Stack>

                {/* Trust Indicators */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6) }}>
                    No credit card required
                  </Typography>
                  <Typography sx={{ mx: 1, color: isDark ? alpha(textColor, 0.5) : alpha(textColor, 0.4) }}>•</Typography>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6) }}>
                    Free 14-day trial
                  </Typography>
                  <Typography sx={{ mx: 1, color: isDark ? alpha(textColor, 0.5) : alpha(textColor, 0.4) }}>•</Typography>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6) }}>
                    Cancel anytime
                  </Typography>
                </Stack>
              </Stack>
            </Fade>
          </Grid>

          {/* Right Content - Stats Cards */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={1500}>
              <Box sx={{ position: 'relative' }}>
                {/* Main Feature Card */}
                <Card
                  sx={{
                    background: cardBg,
                    backdropFilter: 'blur(20px)',
                    borderRadius: 4,
                    border: `1px solid ${alpha(accentColor, isDark ? 0.2 : 0.15)}`,
                    boxShadow: `0 20px 60px ${alpha('#000', isDark ? 0.4 : 0.1)}`,
                    p: 4,
                    animation: `${floatAnimation} 6s ease-in-out infinite`,
                  }}
                >
                  <Grid container spacing={3}>
                    {stats.map((stat, idx) => (
                      <Grid item xs={6} key={idx}>
                        <Zoom in timeout={1800 + idx * 100}>
                          <Card
                            sx={{
                              background: alpha(accentColor, isDark ? 0.1 : 0.05),
                              borderRadius: 2,
                              p: 3,
                              textAlign: 'center',
                              border: `1px solid ${alpha(accentColor, isDark ? 0.2 : 0.1)}`,
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: `0 12px 30px ${alpha(accentColor, 0.25)}`,
                              },
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 900,
                                background: primaryGradient,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1,
                              }}
                            >
                              {stat.value}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDark ? alpha(textColor, 0.7) : alpha(textColor, 0.6),
                                fontWeight: 600,
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </Card>
                        </Zoom>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Feature Highlights */}
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: textColor,
                        mb: 2,
                      }}
                    >
                      Everything You Need, All in One Place
                    </Typography>
                    <Stack spacing={1.5}>
                      {[
                        'Multi-account email management',
                        'Advanced carrier search with filters',
                        'Real-time performance tracking',
                        'Team collaboration & permissions',
                        'Automated email warmup',
                        'Detailed analytics & reports',
                      ].map((feature, idx) => (
                        <Stack direction="row" spacing={1.5} alignItems="center" key={idx}>
                          <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? alpha(textColor, 0.8) : alpha(textColor, 0.7),
                              fontWeight: 500,
                            }}
                          >
                            {feature}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Card>

                {/* Floating Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    background: primaryGradient,
                    borderRadius: '50%',
                    p: 3,
                    boxShadow: `0 8px 32px ${alpha(accentColor, 0.4)}`,
                    animation: `${floatAnimation} 4s ease-in-out infinite`,
                  }}
                >
                  <TrendingUp sx={{ fontSize: 40, color: '#fff' }} />
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default NewHero;
